import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import Chart from "react-apexcharts";
import VuiBox from "components/VuiBox";
import { Card } from "@mui/material";
import VuiTypography from "components/VuiTypography";
import VuiSelect from "components/VuiSelect";
import { useApi } from "hooks/useAPI";
import { QuickBooksService, QuickBooksCashflowData } from "services/QuickBooksService";
import { getLocalTime } from "utils/getLocalTime";
import { type Merchant } from "types/merchant";
import Loading from "components/shared-components/Loading";

const chartOptions = {
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "70%",
      barHeight: "100%",
      distributed: false,
      rangeBarOverlap: true,
      rangeBarGroupRows: false,
      dataLabels: {
        enabled: false,
      },
    },
  },
  fill: {},
  stroke: {
    curve: "smooth",
    width: 2,
  },
};

const monthNames: { [key: string]: string } = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

enum ChartType {
  CASH_BALANCE = "cash_balance",
  MONEY_IN_OUT = "money_in_out",
}

const QuickBooksChart = () => {
  const { get } = useApi();
  const { plotOptions, fill } = chartOptions;
  const [options, setOptions] = useState<any>({});
  const [series, setSeries] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [chartData, setChartData] = useState<QuickBooksCashflowData[]>([]);
  const [chartType, setChartType] = useState<ChartType>(ChartType.CASH_BALANCE);

  const selectedMerchant: Merchant = useSelector(
    (state: any) => state.merchants.selectedMerchant
  );
  const { startDate, endDate } = useSelector((state: any) => state.common);

  useEffect(() => {
    const fetchData = async () => {
      const quickBooksService = new QuickBooksService(get);
      const { startDateString, endDateString } = getLocalTime(
        startDate,
        endDate
      );

      setLoading(true);
      try {
        const result = await quickBooksService.getAccountBalancesChart({
          merchantId: selectedMerchant.id,
          start: startDateString,
          end: endDateString,
        });
        console.log(result);
        setChartData(result.graphData || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching QuickBooks data:", error);
        setChartData([]);
      }
      setLoading(false);
    };

    if (selectedMerchant && startDate && endDate) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, startDate, endDate]);

  const { chartOptions: computedOptions, chartSeries } = useMemo(() => {
    if (chartData.length === 0) {
      return { chartOptions: {}, chartSeries: [] };
    }

    const sortedData = [...chartData].sort((a, b) => 
      new Date(a._id).getTime() - new Date(b._id).getTime()
    );

    let seriesData: any[] = [];
    let maxDataValue = 0;
    let colors: string[] = [];

    if (chartType === ChartType.MONEY_IN_OUT) {
      const inflowData = sortedData.map((d) =>
        Number(d.inflow.toFixed(2))
      );
      const outflowData = sortedData.map((d) =>
        Number(d.outflow.toFixed(2))
      );

      seriesData = [
        {
          name: "Money In",
          type: "column",
          data: inflowData,
        },
        {
          name: "Money Out",
          type: "column",
          data: outflowData,
        },
      ];

      maxDataValue = Math.max(...inflowData, ...outflowData);
      colors = ["#00D4AA", "#FF6B6B"];
    } else {
      const endingBalanceData = sortedData.map((d) =>
        Number(d.endingBalance.toFixed(2))
      );

      seriesData = [
        {
          name: "Cash Balance",
          type: "area",
          data: endingBalanceData,
        },
      ];

      const maxValue = Math.max(...endingBalanceData);
      const minValue = Math.min(...endingBalanceData);
      maxDataValue = Math.max(Math.abs(maxValue), Math.abs(minValue));
      colors = ["#0075FF"];
    }

    let maxValue, minValue, maxY, minY;

    if (chartType === ChartType.MONEY_IN_OUT) {
      const inflowData = sortedData.map((d) => Number(d.inflow.toFixed(2)));
      const outflowData = sortedData.map((d) => Number(d.outflow.toFixed(2)));
      maxValue = Math.max(...inflowData, ...outflowData);
      minValue = 0;
      maxY = maxValue > 0 ? Math.ceil(maxValue / 1000) * 1000 : 1000;
      minY = 0;
    } else {
      const endingBalanceData = sortedData.map((d) => Number(d.endingBalance.toFixed(2)));
      maxValue = Math.max(...endingBalanceData);
      minValue = Math.min(...endingBalanceData);
      maxY = maxValue > 0 ? Math.ceil(maxValue / 1000) * 1000 : 0;
      minY = minValue < 0 ? Math.floor(minValue / 1000) * 1000 : 0;
    }

    const chartOptions = {
      chart: {
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        type: chartType === ChartType.MONEY_IN_OUT ? "column" : "area",
      },
      xaxis: {
        categories: sortedData.map((value) => {
          const parts = value._id.toString().split("-");
          if (parts.length === 3) {
            return `${monthNames[parts[1]]} ${parts[2]}`;
          } else if (parts.length === 2) {
            return `${monthNames[parts[1]]} ${parts[0]}`;
          } else {
            return value._id.toString();
          }
        }),
        labels: {
          style: {
            colors: "#c8cfca",
            fontSize: "10px",
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#c8cfca",
            fontSize: "10px",
          },
          formatter: function (value: number) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: selectedMerchant.currency || "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
        min: minY,
        max: maxY,
        title: {
          text: (chartType === ChartType.CASH_BALANCE) ? "Cash Balance" : "Money in out",
          style: {
            color: "#c8cfca",
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        ...plotOptions,
        bar: {
          ...plotOptions.bar,
        },
      },
      fill,
      stroke: {
        width: 3,
        curve: "smooth",
      },
      colors: colors,
      legend: {
        position: "top",
        horizontalAlign: "left",
        labels: {
          colors: "#c8cfca",
        },
      },
      tooltip: chartType === ChartType.CASH_BALANCE ? {
        theme: "dark",
        shared: true,
        intersect: false,
        custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
          const dataPoint = sortedData[dataPointIndex];
          const endingBalance = series[seriesIndex][dataPointIndex];
          const moneyIn = dataPoint.inflow;
          const moneyOut = dataPoint.outflow;
          const date = w.globals.categoryLabels[dataPointIndex];

          const formatCurrency = (value: number) => {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: selectedMerchant.currency || "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value);
          };

          return `
            <div style="padding: 10px; background: #1a1a1a; border-radius: 6px; color: white;">
              <div style="font-weight: bold; margin-bottom: 8px;">${date}</div>
              <div style="margin-bottom: 4px;">
                <span style="color: #0075FF;">Cash Balance: ${formatCurrency(endingBalance)}</span>
              </div>
              <div style="margin-bottom: 4px;">
                <span style="color: #00D4AA;">Money In: ${formatCurrency(moneyIn)}</span>
              </div>
              <div>
                <span style="color: #FF6B6B;">Money Out: ${formatCurrency(moneyOut)}</span>
              </div>
            </div>
          `;
        },
      } : {
        theme: "dark",
        shared: true,
        intersect: false,
        y: {
          formatter: function (value: number) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: selectedMerchant.currency || "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value);
          },
        },
      },
      grid: {
        strokeDashArray: 5,
        borderColor: "#56577A",
      },
      markers: {
        size: 4,
      },
    };

    return { chartOptions, chartSeries: seriesData };
  }, [chartData, selectedMerchant.currency, chartType]);

  useEffect(() => {
    setOptions(computedOptions);
    setSeries(chartSeries);
  }, [computedOptions, chartSeries]);

  const dropdownControl = (
    <VuiSelect
      //@ts-ignore
      defaultValue={{
        value: chartType,
        label: chartType === ChartType.CASH_BALANCE ? "Cash Balance" : "Money in out",
      }}
      onChange={(option: any) => setChartType(option.value)}
      options={[
        { value: ChartType.CASH_BALANCE, label: "Cash Balance" },
        { value: ChartType.MONEY_IN_OUT, label: "Money in out" },
      ]}
    />
  );

  if (!selectedMerchant?.quickbooksConnected) {
    return (
      <Card>
        <VuiBox sx={{ height: "100%" }}>
          <VuiTypography
            variant="lg"
            color="white"
            fontWeight="bold"
            mb="5px"
            ml="10px"
          >
            Finance report not available, connect your financial account to view data.
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  return (
    <Card>
      <VuiBox sx={{ height: "100%" }}>
        <VuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb="5px"
          ml="10px"
          mr="10px"
        >
          <VuiTypography
            variant="lg"
            color="white"
            fontWeight="bold"
          >
            Finance
          </VuiTypography>
          {dropdownControl}
        </VuiBox>
        <VuiBox sx={{ height: "400px" }}>
          {loading ? (
            <Loading />
          ) : options && series && series.length > 0 ? (
            <Chart
              options={options}
              series={series}
              width="100%"
              height="400"
            />
          ) : (
            <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%">
              <VuiTypography variant="button" color="text">
                No QuickBooks data available for the selected period
              </VuiTypography>
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
    </Card>
  );
};

export default QuickBooksChart;